// Lightweight module-level cache for page data, so switching away from a tab
// and back shows data instantly instead of re-fetching every time.
//
// It lives outside React (module scope), so it survives component unmount/mount.
// Each entry stores the value + when it was fetched. A page reads the cache
// synchronously on mount (instant paint), then refreshes in the background so
// the data stays current.
//
// This is intentionally tiny and dependency-free. No eviction beyond TTL checks
// on read; the set of keys is small and bounded (a handful per page).

type CacheEntry = { value: any; ts: number };

const store: Record<string, CacheEntry> = {};

// Default freshness window. Within this, cached data is used without even a
// background refresh being *required* (callers may still refresh if they want).
const DEFAULT_TTL_MS = 60_000; // 60s

/** Return the cached value for a key, or undefined if absent. */
export function getCached<T = any>(key: string): T | undefined {
  const e = store[key];
  return e ? (e.value as T) : undefined;
}

/** True if the key has a cached value newer than ttlMs. */
export function isFresh(key: string, ttlMs: number = DEFAULT_TTL_MS): boolean {
  const e = store[key];
  return !!e && (Date.now() - e.ts) < ttlMs;
}

/** Store a value for a key, stamping it with the current time. */
export function setCached(key: string, value: any): void {
  store[key] = { value, ts: Date.now() };
}

/** Drop a cached key (e.g. after a mutation that invalidates it). */
export function invalidate(key: string): void {
  delete store[key];
}

/**
 * Cache-aware fetch helper.
 *
 * Usage in a page:
 *   const parts = getCached('parts');          // instant paint if present
 *   if (parts) setParts(parts);
 *   swrLoad('parts', () => fetchAll('/api/inventory/parts/'), setParts);
 *
 * swrLoad = "stale-while-revalidate": if a cached value exists it is applied
 * immediately by the caller (above), then this refreshes in the background and
 * updates via onData. If nothing is cached, it fetches and applies once.
 */
export async function swrLoad<T = any>(
  key: string,
  fetcher: () => Promise<T>,
  onData: (v: T) => void,
  opts?: { force?: boolean; ttlMs?: number },
): Promise<void> {
  const ttl = opts?.ttlMs ?? DEFAULT_TTL_MS;
  // If we have a fresh value and aren't forcing, skip the network entirely.
  if (!opts?.force && isFresh(key, ttl)) {
    const v = getCached<T>(key);
    if (v !== undefined) { onData(v); return; }
  }
  try {
    const v = await fetcher();
    setCached(key, v);
    onData(v);
  } catch (e) {
    // On failure, keep whatever was cached; surface nothing new.
  }
}
